<script setup lang="ts">
import { computed, ref } from "vue";
import { formatCurrency } from "@/v2Projects/utils/display";

export interface LiabilitySegment {
  key: string;
  label: string;
  cents: number;
  /** Optional "N experts" style suffix shown after the amount in the legend. */
  countLabel?: string;
  colorClass: string;
  textClass: string;
}

const props = withDefaults(defineProps<{
  segments: LiabilitySegment[];
  currency?: string;
  activeKey?: string;
  minSegmentPct?: number;
}>(), {
  minSegmentPct: 2,
});

const emit = defineEmits<{
  select: [key: string];
}>();

const hoveredKey = ref<string | null>(null);

const total = computed(() => props.segments.reduce((sum, segment) => sum + Math.max(segment.cents, 0), 0));

const visibleSegments = computed(() =>
  props.segments
    .map((segment) => ({
      ...segment,
      pct: total.value > 0 ? (Math.max(segment.cents, 0) / total.value) * 100 : 0,
    }))
    .filter((segment) => segment.cents > 0),
);

function isEmphasized(key: string) {
  return hoveredKey.value === key || props.activeKey === key;
}

function isDimmed(key: string) {
  return hoveredKey.value !== null && hoveredKey.value !== key;
}

function segmentClasses(key: string) {
  return [
    "relative h-full rounded-full border-0 p-0 transform-gpu cursor-pointer origin-center transition duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300",
    isEmphasized(key) ? "z-10 -translate-y-px scale-y-[1.8] saturate-125 brightness-105" : "",
    isDimmed(key) ? "opacity-25 grayscale saturate-50 brightness-90" : "",
  ];
}

function itemClasses(key: string) {
  return [
    "inline-flex items-center gap-1.5 rounded-full bg-transparent px-2 py-1 text-xs ring-0 transition-[background-color,color,opacity] duration-200",
    isEmphasized(key) ? "bg-slate-50 ring-1 ring-slate-200 shadow-sm" : "",
    isDimmed(key) ? "opacity-40" : "hover:bg-slate-50 hover:ring-1 hover:ring-slate-200 hover:shadow-sm",
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-300",
  ];
}
</script>

<template>
  <div class="flex min-w-0 flex-col gap-2.5">
    <div class="isolate flex h-2 gap-[1.5px] overflow-visible rounded-full bg-gray-100">
      <button
        v-for="segment in visibleSegments"
        :key="segment.key"
        type="button"
        :class="[segment.colorClass, ...segmentClasses(segment.key)]"
        :style="{ width: `${Math.max(segment.pct, minSegmentPct)}%` }"
        :aria-label="`${segment.label}: ${formatCurrency(segment.cents, currency)}`"
        @mouseenter="hoveredKey = segment.key"
        @mouseleave="hoveredKey = null"
        @focus="hoveredKey = segment.key"
        @blur="hoveredKey = null"
        @click="emit('select', segment.key)"
      />
    </div>

    <div class="flex flex-wrap gap-x-4 gap-y-2">
      <button
        v-for="segment in segments"
        :key="segment.key"
        type="button"
        :class="itemClasses(segment.key)"
        @mouseenter="hoveredKey = segment.key"
        @mouseleave="hoveredKey = null"
        @focus="hoveredKey = segment.key"
        @blur="hoveredKey = null"
        @click="emit('select', segment.key)"
      >
        <span class="h-1.5 w-1.5 shrink-0 rounded-full" :class="segment.colorClass" />
        <span class="text-gray-500 transition-colors duration-200" :class="{ 'text-gray-900': isEmphasized(segment.key) }">
          {{ segment.label }}
        </span>
        <span class="font-bold tabular-nums" :class="segment.textClass">
          {{ formatCurrency(segment.cents, currency) }}
        </span>
        <span v-if="segment.countLabel" class="text-gray-400">· {{ segment.countLabel }}</span>
      </button>
    </div>
  </div>
</template>
